import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom'
import {
	Anchor,
	Box,
	Title,
	Text,
	Button,
	Table,
	Modal,
	TextInput,
	Select,
	Group,
} from '@mantine/core'
import {
	createOrgUser,
	deleteUserRole,
	reactivateOrgUser,
	resendUserInvite,
} from '../services/actions/userActions'
import {
	getOrgBillingDashboard,
	getOrgDetails,
	getOrgUsers,
} from '../services/actions/orgActions'

const ManageUsersPage = () => {
	const { orgSlug } = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const currentOrgDetails = useSelector((state) => state.org.orgDetails)

	const [users, setUsers] = useState([])
	const [managedOrg, setManagedOrg] = useState(null)
	const [planLimits, setPlanLimits] = useState(null)
	const [error, setError] = useState('')
	const [showDeactivated, setShowDeactivated] = useState(false)
	const [showModal, setShowModal] = useState(false)
	const [newUser, setNewUser] = useState({
		name: '',
		email: '',
		role: 'user',
	})

	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
	const [selectedUser, setSelectedUser] = useState(null)
	const [reactivatePromptOpen, setReactivatePromptOpen] = useState(false)
	const [reactivateCandidate, setReactivateCandidate] = useState(null)
	const [pendingReactivateRole, setPendingReactivateRole] = useState(null)

	const fetchUsersAndLimits = useCallback(async () => {
		if (!orgSlug) return

		try {
			const [usersData, billingData, orgData] = await Promise.all([
				getOrgUsers(orgSlug),
				getOrgBillingDashboard(orgSlug),
				getOrgDetails(orgSlug),
			])
			setUsers(usersData)
			setPlanLimits(billingData?.planLimits || null)
			setManagedOrg(orgData || null)
		} catch (err) {
			console.error('Failed to load users:', err)
			setError('Failed to load users.')
		}
	}, [orgSlug])

	useEffect(() => {
		fetchUsersAndLimits()
	}, [fetchUsersAndLimits])

	const userLimit = planLimits?.canAddUser || {}
	const adminLimit = planLimits?.canAddAdmin || {}
	const userLimitReached = userLimit?.allowed === false
	const adminLimitReached = adminLimit?.allowed === false
	const managedOrgId =
		(managedOrg?.slug === orgSlug ? managedOrg.id : null) ||
		(currentOrgDetails?.slug === orgSlug ? currentOrgDetails.id : null)
	const inControlRoute = location.pathname.startsWith('/control')
	const billingPath = inControlRoute
		? `/control/orgs/${orgSlug}/billing`
		: `/${orgSlug}/admin/billing`
	const activeUsers = users.filter((user) => user.isActive !== false)
	const sortedUsers = [...users].sort((a, b) => {
		const aActive = a.isActive !== false
		const bActive = b.isActive !== false
		if (aActive === bActive) return 0
		return aActive ? -1 : 1
	})
	const selectedSearchUserId = new URLSearchParams(location.search).get('user')
	const visibleUsers = selectedSearchUserId
		? sortedUsers.filter((user) => user.id === selectedSearchUserId)
		: showDeactivated ? sortedUsers : activeUsers
	const selectedUserWillHardDelete =
		selectedUser && selectedUser.hasSetPassword === false

	const handleCreateUser = async (e) => {
		e.preventDefault()
		setError('')
		if (!managedOrgId) {
			setError('Organization is still loading. Please try again.')
			return
		}
		try {
			await createOrgUser({ ...newUser, org: managedOrgId })
			setShowModal(false)
			setNewUser({ name: '', email: '', role: 'user' })
			await fetchUsersAndLimits()
		} catch (err) {
			console.error('Failed to create user:', err)
			if (err.code === 'DEACTIVATED_USER_EXISTS') {
				setShowModal(false)
				setReactivateCandidate(
					err.user || {
						name: newUser.name,
						email: newUser.email,
					}
				)
				setPendingReactivateRole(newUser.role)
				setReactivatePromptOpen(true)
				return
			}
			setError(err.message || 'Failed to create user.')
		}
	}

	const handleRemoveUser = async () => {
		if (!selectedUser) return
		setError('')
		if (!managedOrgId) {
			setError('Organization is still loading. Please try again.')
			return
		}
		try {
			await deleteUserRole(selectedUser.id, managedOrgId)
			setConfirmDeleteOpen(false)
			setSelectedUser(null)
			await fetchUsersAndLimits()
		} catch (err) {
			console.error('Failed to delete role:', err)
			setError(err.message || 'Failed to remove user from role.')
		}
	}

	const handleReactivateUser = async (
		user = reactivateCandidate,
		role = pendingReactivateRole || user?.role
	) => {
		if (!user?.id) return
		setError('')
		if (!managedOrgId) {
			setError('Organization is still loading. Please try again.')
			return
		}

		try {
			await reactivateOrgUser(user.id, { role, orgId: managedOrgId })
			setReactivatePromptOpen(false)
			setReactivateCandidate(null)
			setPendingReactivateRole(null)
			setShowModal(false)
			setNewUser({ name: '', email: '', role: 'user' })
			await fetchUsersAndLimits()
		} catch (err) {
			console.error('Failed to reactivate user:', err)
			setError(err.message || 'Failed to reactivate user.')
		}
	}

	const getAuthenticationMethod = (user) => {
		const authMethod =
			user.authMethod || user.authenticationMethod || user.loginProvider

		if (typeof authMethod === 'string') {
			return authMethod.toLowerCase().includes('google') ? 'Google' : 'Password'
		}

		return user.isGoogleUser ? 'Google' : 'Password'
	}

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Organization Users
			</Title>

			{selectedSearchUserId && <Text size='sm' color='dimmed' mb='md'>
				Showing the selected user. <Anchor component={Link} to={location.pathname}>View all users</Anchor>
			</Text>}

			{error && (
				<Text color='red' mb='md'>
					{error}
				</Text>
			)}

			{(userLimitReached || adminLimitReached) && (
				<Text c='orange' mb='sm'>
					{userLimitReached
						? `User limit reached (${userLimit.currentUsers}/${userLimit.maxUsers}). `
						: ''}
					{adminLimitReached
						? `Admin limit reached (${adminLimit.currentAdmins}/${adminLimit.maxAdmins}). `
						: ''}
					<Anchor component={Link} to={billingPath} size='sm'>
						Review Plan & Billing
					</Anchor>
				</Text>
			)}

			<Group mb='lg' spacing='sm' align='center'>
				<Button
					onClick={() => setShowModal(true)}
					disabled={userLimitReached || !managedOrgId}
				>
					➕ Create New User
				</Button>
				{!selectedSearchUserId && <Anchor
					component='button'
					type='button'
					size='sm'
					onClick={() => setShowDeactivated((value) => !value)}
				>
					{showDeactivated ? 'Hide deactivated' : 'Show deactivated'}
				</Anchor>}
			</Group>

			{visibleUsers?.length > 0 ? (
				<Table withBorder highlightOnHover striped>
					<thead>
						<tr>
							<th>Name</th>
							<th>Email</th>
							<th>Authentication Method</th>
							<th>Role</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{visibleUsers.map((user) => (
							<tr
								key={user.id}
								style={
									user.isActive === false
										? {
												backgroundColor: '#f8f9fa',
												color: '#868e96',
										  }
										: undefined
								}
							>
								<td>{user.name}</td>
								<td>{user.email}</td>
								<td>{getAuthenticationMethod(user)}</td>
								<td>
									{user.role === 'admin'
										? 'Admin'
										: user.role === 'user'
										? 'User'
										: user.role}
								</td>
								<td style={{ textAlign: 'right' }}>
									<div
										style={{
											display: 'flex',
											justifyContent: 'flex-end',
											gap: '8px',
										}}
									>
										{user.isActive === false ? (
											<Button
												size='xs'
												variant='light'
												color='green'
												onClick={() => handleReactivateUser(user, user.role)}
											>
												Reactivate
											</Button>
										) : (
											<>
												{!user.hasSetPassword && (
													<Button
														size='xs'
														variant='light'
														color='blue'
														onClick={async () => {
															if (!managedOrgId) {
																setError(
																	'Organization is still loading. Please try again.'
																)
																return
															}
															try {
																await resendUserInvite(user.id, managedOrgId)
																alert(`Invite resent to ${user.email}`)
															} catch (err) {
																alert(err.message || 'Failed to resend invite')
																console.error(err)
															}
														}}
													>
														Resend Invite
													</Button>
												)}
												<Button
													size='xs'
													variant='light'
													onClick={() =>
														navigate(`/${orgSlug}/admin/users/${user.id}/edit`)
													}
												>
													Edit
												</Button>
											</>
										)}
										{(user.isActive !== false || !user.hasSetPassword) && (
											<Button
												size='xs'
												variant='light'
												color='red'
												onClick={() => {
													setSelectedUser(user)
													setConfirmDeleteOpen(true)
												}}
											>
												{user.hasSetPassword ? 'Deactivate' : 'Delete'}
											</Button>
										)}
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			) : (
				<Text>No active users found.</Text>
			)}

			{/* Create User Modal */}
			<Modal
				opened={showModal}
				onClose={() => setShowModal(false)}
				title='Create New User'
				centered
			>
				<form onSubmit={handleCreateUser}>
					<TextInput
						label='Name'
						value={newUser.name}
						onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
						required
						mb='sm'
					/>
					<TextInput
						label='Email'
						type='email'
						value={newUser.email}
						onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
						required
						mb='sm'
					/>
					<Select
						label='Role'
						value={newUser.role}
						onChange={(value) => setNewUser({ ...newUser, role: value })}
						data={[
							{ value: 'user', label: 'User' },
							{ value: 'admin', label: 'Admin' },
						]}
						required
					/>
					<Group mt='lg' position='right'>
						<Button variant='light' onClick={() => setShowModal(false)}>
							Cancel
						</Button>
						<Button type='submit'>Save</Button>
					</Group>
				</form>
			</Modal>

			{/* Confirm Remove Modal */}
			<Modal
				opened={confirmDeleteOpen}
				onClose={() => setConfirmDeleteOpen(false)}
				title={selectedUserWillHardDelete ? 'Confirm Delete' : 'Confirm Deactivation'}
				centered
			>
				<Text mb='md'>
					{selectedUserWillHardDelete ? (
						<>
							Are you sure you want to permanently delete{' '}
							<strong>{selectedUser?.name}</strong> and remove their assigned
							roles?
						</>
					) : (
						<>
							Are you sure you want to deactivate{' '}
							<strong>{selectedUser?.name}</strong>?
						</>
					)}
				</Text>
				<Group position='right'>
					<Button variant='light' onClick={() => setConfirmDeleteOpen(false)}>
						Cancel
					</Button>
					<Button color='red' onClick={handleRemoveUser}>
						{selectedUserWillHardDelete ? 'Yes, Delete' : 'Yes, Deactivate'}
					</Button>
				</Group>
			</Modal>

			<Modal
				opened={reactivatePromptOpen}
				onClose={() => {
					setReactivatePromptOpen(false)
					setReactivateCandidate(null)
					setPendingReactivateRole(null)
				}}
				title='Reactivate User'
				centered
			>
				<Text mb='md'>
					This user already exists as a deactivated user in your organization,
					would you like to reactivate them?
				</Text>
				<Group position='right'>
					<Button
						variant='light'
						onClick={() => {
							setReactivatePromptOpen(false)
							setReactivateCandidate(null)
							setPendingReactivateRole(null)
						}}
					>
						Cancel
					</Button>
					<Button
						color='green'
						onClick={() =>
							handleReactivateUser(
								reactivateCandidate,
								pendingReactivateRole
							)
						}
					>
						Reactivate
					</Button>
				</Group>
			</Modal>
		</Box>
	)
}

export default ManageUsersPage
